import { useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '@/auth/AuthProvider';
import { TransitionToasts } from '@/components/common/TransitionToasts';
import { Toaster } from '@/components/ui/sonner';
import { CdnFeedbackWidget as FeedbackWidget } from '@/lib/cdnFeedbackWidget';
import {
  FEEDBACK_BACKEND_URL,
  FEEDBACK_ENVIRONMENT,
  FEEDBACK_URL,
  UI_FEEDBACK_MAX_CHARS,
  UI_FEEDBACK_MIN_CHARS,
  buildFeedbackExtras,
} from '@/lib/feedback';
import { Sidebar } from './Sidebar';
import { Header } from './Header';

export function AppShell(): JSX.Element {
  const auth = useAuth();
  const location = useLocation();
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [mobileNavigationOpen, setMobileNavigationOpen] = useState(false);
  const route = `${location.pathname}${location.search}${location.hash}`;
  const feedbackExtras = buildFeedbackExtras(auth.principal, route, auth.sessionId);

  return (
    <>
      <div className="flex h-screen w-full">
        {mobileNavigationOpen ? (
          <button
            type="button"
            className="fixed inset-0 z-40 bg-black/40 md:hidden"
            aria-label="Dismiss navigation"
            onClick={() => setMobileNavigationOpen(false)}
          />
        ) : null}
        <Sidebar
          mobileOpen={mobileNavigationOpen}
          onNavigate={() => setMobileNavigationOpen(false)}
          onFeedbackClick={() => {
            setMobileNavigationOpen(false);
            setFeedbackOpen(true);
          }}
        />
        <div className="flex min-w-0 flex-1 flex-col">
          <TransitionToasts />
          <Header onOpenNavigation={() => setMobileNavigationOpen(true)} />
          <main className="min-h-0 min-w-0 flex-1 overflow-auto bg-background px-3 sm:px-6">
            <Outlet />
          </main>
        </div>
      </div>
      <Toaster
        className="cockpit-notifications"
        position="bottom-left"
        visibleToasts={1}
        closeButton
        richColors
      />
      {auth.sessionId ? (
        <FeedbackWidget
          open={feedbackOpen}
          onOpenChange={setFeedbackOpen}
          hideLauncher
          sessionId={auth.sessionId}
          appId="techmer-sales-order-automation"
          customerName="Techmer"
          environment={FEEDBACK_ENVIRONMENT}
          feedbackUrl={FEEDBACK_URL}
          backendUrl={FEEDBACK_BACKEND_URL}
          {...(auth.principal?.displayName ? { userName: auth.principal.displayName } : {})}
          {...(auth.principal?.username ? { userEmail: auth.principal.username } : {})}
          feedbackMinChars={UI_FEEDBACK_MIN_CHARS}
          feedbackMaxChars={UI_FEEDBACK_MAX_CHARS}
          extras={feedbackExtras}
          getExtras={({ feedbackType, message }) => ({
            ...feedbackExtras,
            pageUrl: window.location.href,
            pageTitle: document.title,
            route,
            selectedFeedbackType: feedbackType,
            commentLength: message.length,
            submittedAt: new Date().toISOString(),
          })}
        />
      ) : null}
    </>
  );
}
